/**
 * 今日の県のランキング（クライアント側）。
 *
 * サーバーは functions/api/daily.ts（Cloudflare Pages Functions + D1）。
 * サーバーが未設定・通信できないときは、`status: 'unavailable'` を返すだけでゲームは止めない。
 */

import { apiUrl } from '../platform/native';
import { metaStorageKey } from '../game/config/gameConfig';
import type { StorageLike } from '../game/systems/StorageSystem';
import { composeDailyName, makeDailyName, randomNameNumber, splitDailyName } from './dailyNames';
import { JsonStore } from './jsonStore';

export interface RankingIdentity {
  player: string;
  name: string;
}

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function normalize(raw: unknown): RankingIdentity {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    player: typeof r.player === 'string' && r.player ? r.player : randomId(),
    name: typeof r.name === 'string' && r.name ? r.name : makeDailyName(),
  };
}

export class RankingIdentityStore {
  private readonly store: JsonStore<RankingIdentity>;
  constructor(storage?: StorageLike | null) {
    this.store = new JsonStore(metaStorageKey('ranking'), normalize, storage);
    // 初回に作ったIDを保存しておく
    this.store.update(() => undefined);
  }
  get identity(): RankingIdentity {
    return this.store.data;
  }
  /**
   * 前半・後半を一覧から選んで呼び名にする（自由入力はさせない）。番号は今のものを引き継ぎ、無ければ自動で付ける。
   * 一覧にない語なら何もしないで null。
   */
  chooseName(adjective: string, noun: string): string | null {
    const number = splitDailyName(this.identity.name)?.number ?? randomNameNumber();
    const name = composeDailyName(adjective, noun, number);
    if (!name) return null;
    return this.store.update((d) => {
      d.name = name;
    }).name;
  }

  /** 呼び名を振り直す（自由入力はさせない）。 */
  rerollName(): string {
    return this.store.update((d) => {
      d.name = makeDailyName();
    }).name;
  }
}

export interface RankingBoard {
  status: 'ok';
  total: number;
  top: Array<{ name: string; score: number; maxLevel: number }>;
  me: { rank: number; score: number; name: string } | null;
  /** テスト用モックの見本（本物のサーバーの値ではない）。 */
  sample?: boolean;
}
export type RankingResult = RankingBoard | { status: 'unavailable' };

// アプリの中では本番の Web 版の API に向ける（native.ts）
const API = apiUrl('/api/daily');

export async function submitDailyScore(
  identity: RankingIdentity,
  entry: { date: string; region: string; score: number; maxLevel: number; drops: number; playMs: number },
): Promise<boolean> {
  try {
    const res = await fetch(API, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...entry, player: identity.player, name: identity.name }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchDailyBoard(
  identity: RankingIdentity,
  date: string,
  region: string,
): Promise<RankingResult> {
  try {
    const q = new URLSearchParams({ date, region, player: identity.player });
    const res = await fetch(`${API}?${q}`, { cache: 'no-store' });
    if (!res.ok) return { status: 'unavailable' };
    const type = res.headers.get('content-type') ?? '';
    // Pages Functions が無い環境（開発サーバー・古いデプロイ）では index.html が返ってくる
    if (!type.includes('application/json')) return { status: 'unavailable' };
    const body = (await res.json()) as Omit<RankingBoard, 'status'>;
    return { status: 'ok', total: body.total, top: body.top, me: body.me };
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * 結果画面に出す「今日の順位」の一文。
 * 上位20人の点が分かるので、一つ上（20位より下なら20位）まであと何点かを添える。
 */
export function describeDailyRank(board: RankingBoard): string | null {
  const me = board.me;
  if (!me) return null;
  const head = `今日の順位 ${formatDailyRank(me.rank, board.total)}`;
  if (me.rank <= 1) {
    // 人数が少ないときは formatDailyRank の側で「一番乗り！」「いまトップ」と言っている
    return board.total <= FEW_PLAYERS ? head : `${head} ・ いまトップです`;
  }
  // 一つ上の人が上位の一覧に入っていればその人、20位より下なら20位の人と比べる
  const targetRank = Math.min(me.rank - 1, 20);
  const target = board.top[targetRank - 1];
  if (!target || target.score < me.score) return head;
  const gap = target.score - me.score + 1;
  return `${head} ・ あと${gap.toLocaleString('ja-JP')}点で${targetRank}位`;
}

/**
 * これ以下の人数のときは「○人中○位」と言わない。公開直後や遊ぶ人の少ない県で
 * 「1人中1位」「3人中3位」と出ると、さみしい・負けた印象になる（2026-10-05 レビュー5）。
 */
export const FEW_PLAYERS = 3;

/**
 * 「128人中5位」。結果画面と共有の文面で同じ書き方にする。
 * 人数が少ないとき（3人以下）は前向きな言い方にする：
 * 1人 →「一番乗り！」、1位 →「いまトップ・参加2人」、それ以外 →「早めの参加・いま3人で2位」
 * （共有の文面では「（…）」で囲むので、中にかっこを使わない）
 */
export function formatDailyRank(rank: number, total: number): string {
  if (total <= FEW_PLAYERS) {
    if (total <= 1) return '一番乗り！';
    if (rank <= 1) return `いまトップ・参加${total}人`;
    return `早めの参加・いま${total}人で${rank}位`;
  }
  return `${total.toLocaleString('ja-JP')}人中${rank.toLocaleString('ja-JP')}位`;
}

/**
 * テスト用モック（`?platform=mock`。localhost・staging だけで有効）の見本の順位。
 * ランキングのサーバーが無い環境でも、結果画面と共有文の見た目を確かめられるようにする。
 * 本番の Web 版・アプリでは使わない（呼ぶ側が platform.kind === 'mock' のときだけ使う）。
 */
export function sampleBoardForMock(score: number): RankingBoard {
  const total = 128;
  const rank = Math.max(1, Math.min(total, 40 - Math.floor(score / 300)));
  const top = Array.from({ length: 20 }, (_, i) => ({
    name: `見本${i + 1}`,
    score: Math.max(score + 1, 12_000 - i * 400),
    maxLevel: 8,
  }));
  return { status: 'ok', total, top, me: { rank, score, name: '見本' }, sample: true };
}
